import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Loader2, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { errMsg, useApi } from "@/lib/api";
import { DatePick } from "@/components/atas/DatePick";
import { OfficialTemplates, UseTemplateButton } from "@/components/program/Templates";
import { TipBanner } from "@/components/atas/TipBanner";

const BULAN = ["Jan", "Feb", "Mac", "Apr", "Mei", "Jun", "Jul", "Ogo", "Sep", "Okt", "Nov", "Dis"];
const tarikh = (iso) => { const d = new Date(iso); return `${d.getDate()} ${BULAN[d.getMonth()]} ${d.getFullYear()}`; };
const inputCls = "h-11 px-3 rounded-xl bg-surface border border-line text-sm focus:outline-none focus:border-[var(--q)]";

const STATUS = { draf: "Draf", sedia: "Sedia", selesai: "Selesai" };
const act = "h-8 px-3 rounded-full border border-line text-xs inline-flex items-center hover:bg-bg hover:text-q transition-colors";

const ProgramRow = ({ k, onDelete }) => (
  <li className="surface rounded-2xl p-4 sm:p-5 fade-up" data-testid={`program-row-${k.id}`}>
    <div className="flex items-start gap-4">
      <Link to={`/kajian/${k.id}`} className="flex-1 min-w-0 group" data-testid={`kajian-open-${k.id}`}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono-data text-[11px] px-2 py-0.5 rounded-full bg-i-soft text-i uppercase">Program</span>
          <span className="font-mono-data text-[11px] px-2 py-0.5 rounded-full border border-line text-mute" data-testid={`program-row-status-${k.id}`}>{STATUS[k.status] || "Draf"}</span>
          {k.parent_kajian_id && <span className="font-mono-data text-[11px] text-mute">versi susulan</span>}
          {k.templat && <span className="font-mono-data text-[11px] px-2 py-0.5 rounded-full bg-q-soft text-q" data-testid={`program-row-template-${k.id}`}>{k.rasmi ? "Templat Rasmi" : "Templat"}</span>}
          {k.peserta_aktif && <span className="font-mono-data text-[11px] px-2 py-0.5 rounded-full bg-i-soft text-i" data-testid={`program-row-shared-${k.id}`}>Dikongsi</span>}
          {k.dari_templat && <span className="font-mono-data text-[11px] text-mute">daripada templat: {k.dari_templat.tajuk}</span>}
        </div>
        <div className="font-display text-lg text-ink mt-2 group-hover:text-q transition-colors">{k.tajuk}</div>
        <p className="text-sm text-mute mt-1 line-clamp-1">{k.program?.peserta}</p>
        <div className="font-mono-data text-[11px] text-mute mt-2">{k.rujukan} · Dikemas kini {tarikh(k.updated_at)} · {k.aar_count} AAR · {k.version_count - 1} suntingan · {k.anak.length} versi baharu</div>
      </Link>
      <button onClick={() => onDelete(k)} aria-label="Padam" data-testid={`kajian-delete-${k.id}`}
        className="h-10 w-10 grid place-items-center rounded-full text-mute hover:text-warn hover:bg-warn-soft transition-colors shrink-0"><Trash2 size={16} /></button>
    </div>
    <div className="flex flex-wrap gap-2 mt-3">
      {k.templat && <UseTemplateButton id={k.id} testid={`program-use-template-${k.id}`} className="!h-8 !px-3 !text-xs" />}
      <Link to={`/kajian/${k.id}`} className={act} data-testid={`program-reopen-${k.id}`}>Buka Semula</Link>
      <Link to={`/program?dari=${k.id}&jenis=peserta`} className={act} data-testid={`program-adapt-${k.id}`}>Sesuaikan Peserta</Link>
      <Link to={`/kajian/${k.id}#aar`} className={act} data-testid={`program-aar-${k.id}`}>Catat AAR</Link>
      <Link to={`/program?dari=${k.id}`} className={act} data-testid={`program-newversion-${k.id}`}>Bina Versi Baharu</Link>
    </div>
  </li>
);

const KajianRow = ({ k, onDelete }) => (
  <li className="surface rounded-2xl p-4 sm:p-5 flex items-start gap-4 fade-up" data-testid={`kajian-row-${k.id}`}>
    <Link to={`/kajian/${k.id}`} className="flex-1 min-w-0 group" data-testid={`kajian-open-${k.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono-data text-[11px] px-2 py-0.5 rounded-full bg-q-soft text-q uppercase">Kajian {k.skill}</span>
        <span className="font-mono-data text-[11px] text-mute">{k.mod === "bumi" ? "Bumi → Langit → Bumi" : "Langit → Bumi"}</span>
      </div>
      <div className="font-display text-lg text-ink mt-2 group-hover:text-q transition-colors">{k.rujukan}</div>
      {k.isu && <p className="text-sm text-mute mt-1 line-clamp-2">{k.isu}</p>}
      <div className="font-mono-data text-[11px] text-mute mt-2">{tarikh(k.updated_at)} · {k.konteks} · {k.version_count} versi</div>
    </Link>
    <div className="flex flex-col items-end gap-2 shrink-0">
      <button onClick={() => onDelete(k)} aria-label="Padam kajian" data-testid={`kajian-delete-${k.id}`}
        className="h-10 w-10 grid place-items-center rounded-full text-mute hover:text-warn hover:bg-warn-soft transition-colors"><Trash2 size={16} /></button>
      <Link to={`/kajian/${k.id}`} className={act} data-testid={`kajian-continue-${k.id}`}>Sambung Kajian</Link>
    </div>
  </li>
);

const Row = ({ k, onDelete }) => (k.jenis === "program" ? <ProgramRow k={k} onDelete={onDelete} /> : <KajianRow k={k} onDelete={onDelete} />);

export default function KajianListPage() {
  const api = useApi();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [skill, setSkill] = useState("semua");
  const [jenis, setJenis] = useState("semua");
  const [dari, setDari] = useState("");
  const [hingga, setHingga] = useState("");
  const [skills, setSkills] = useState([]);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    try { setItems(await api("get", "/kajian", null, { params: { q, skill: skill === "semua" ? "" : skill, dari, hingga } })); }
    catch (e) { toast.error(errMsg(e)); setItems([]); }
  }, [api, q, skill, dari, hingga]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api("get", "/skills").then(setSkills).catch(() => {}); }, [api]);

  const confirmDelete = async () => {
    try { await api("delete", `/kajian/${toDelete.id}`); setItems((xs) => xs.filter((x) => x.id !== toDelete.id)); toast.success("Kajian dipadam"); }
    catch (e) { toast.error(errMsg(e)); } finally { setToDelete(null); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10 sm:py-14 space-y-8">
      <header className="fade-up">
        <div className="eyebrow">Simpanan peribadi</div>
        <h1 className="font-display text-4xl sm:text-5xl text-ink mt-3">Ruang Kerja Saya</h1>
        <p className="text-sm text-mute mt-3">Program latihan dan kajian HTE anda. Membuka, menyunting dan mencatat AAR tidak menggunakan kuota.</p>
        <div className="flex flex-wrap gap-2 mt-5">
          <Link to="/program" className="h-10 px-5 inline-flex items-center rounded-full bg-ink text-bg text-sm font-semibold" data-testid="workspace-new-program">Saya Ada Program</Link>
          <Link to="/hte" className="h-10 px-5 inline-flex items-center rounded-full border border-line text-sm" data-testid="workspace-new-kajian">Kaji Ayat</Link>
        </div>
      </header>
      <TipBanner testid="workspace-tip" />
      <OfficialTemplates />
      <div className="flex gap-2" data-testid="workspace-type-filter">
        {[["semua", "Semua"], ["program", "Program"], ["templat", "Templat"], ["kajian", "Kajian"]].map(([v, l]) => (
          <button key={v} onClick={() => setJenis(v)} data-testid={`workspace-filter-${v}`}
            className={`h-9 px-4 rounded-full text-sm border transition-colors ${jenis === v ? "bg-ink text-bg border-transparent" : "border-line text-mute hover:text-ink"}`}>{l}</button>
        ))}
      </div>
      <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
        <label className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-mute" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari tajuk, peserta, rujukan atau isu…" data-testid="kajian-search-input" className={`${inputCls} w-full pl-9`} />
        </label>
        <Select value={skill} onValueChange={setSkill}>
          <SelectTrigger className="h-11 rounded-xl sm:w-48" data-testid="kajian-skill-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="semua">Semua kemahiran</SelectItem>
            {skills.filter((s) => s.status === "aktif").map((s) => <SelectItem key={s.id} value={s.id}>{s.nama}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex flex-wrap items-center gap-2 sm:col-span-2 text-sm text-mute">
          <span>Dari</span><DatePick value={dari} onChange={setDari} placeholder="Pilih tarikh" testid="kajian-date-from" />
          <span>hingga</span><DatePick value={hingga} onChange={setHingga} placeholder="Pilih tarikh" testid="kajian-date-to" />
        </div>
      </div>
      {!items ? <Loader2 className="animate-spin text-q" /> : items.length === 0 ? (
        <div className="surface rounded-2xl p-10 text-center" data-testid="kajian-empty">
          <FileText className="mx-auto text-mute" />
          <p className="text-mute mt-3 text-sm">Tiada kajian ditemui.</p>
          <Link to="/program" className="inline-flex mt-4 h-10 px-5 items-center rounded-full bg-ink text-bg text-sm font-semibold">Mulakan program pertama</Link>
        </div>
      ) : <ul className="space-y-3" data-testid="kajian-list">{items.filter((k) => jenis === "semua" || (jenis === "templat" ? k.templat : (jenis === "program") === (k.jenis === "program"))).map((k) => <Row key={k.id} k={k} onDelete={setToDelete} />)}</ul>}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="kajian-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Padam kajian ini?</AlertDialogTitle>
            <AlertDialogDescription>{toDelete?.rujukan} dan semua versinya akan dipadam secara kekal.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="kajian-delete-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="kajian-delete-confirm">Padam</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
